<template>
    <div class="table-search-product-autocomplete flex-grow-1">
        <v-text-field
            ref="field"
            :value="query"
            class="table-search-product-autocomplete__field"
            :class="{
                'table-search-product-autocomplete__field--filled': isQueryFilled,
            }"
            dense
            outlined
            hide-details
            single-line
            clearable
            prepend-inner-icon="mdi-magnify"
            :placeholder="placeholder"
            :aria-label="placeholder"
            :autofocus="autofocus"
            :loading="loading"
            @input="onQueryInput"
            @focus="onFocus"
            @blur="onBlur"
            @click:clear="onClear"
            @keydown.down.prevent="focusNext"
            @keydown.up.prevent="focusPrevious"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.esc.prevent="closeMenu"
        />
        <v-card
            v-if="menuOpen && (loading || suggestions.length || query.length >= minChars)"
            class="table-search-product-autocomplete__menu"
            elevation="4"
            @mousedown.prevent
        >
            <v-list dense class="py-0">
                <v-list-item v-if="loading" disabled>
                    <v-list-item-title class="text-caption grey--text">
                        {{ $translate("loading") }}
                    </v-list-item-title>
                </v-list-item>
                <v-list-item
                    v-else-if="query.length >= minChars && !suggestions.length"
                    disabled
                >
                    <v-list-item-title class="text-caption grey--text">
                        {{ $translate("noResults") }}
                    </v-list-item-title>
                </v-list-item>
                <v-list-item
                    v-for="(product, index) in suggestions"
                    :key="product.id"
                    :class="{
                        'table-search-product-autocomplete__item--active':
                            index === highlightedIndex,
                    }"
                    @mousedown.prevent="selectProduct(product)"
                    @mouseenter="highlightedIndex = index"
                >
                    <v-list-item-content class="py-2">
                        <ProductName
                            class="text-body-2"
                            :product="product"
                            :display-code="true"
                            :inline-code="true"
                        />
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import ProductName from "@/components/common/product-name";

export default {
    name: "TableSearchProductAutocomplete",
    mixins: [GlobalMixins],
    components: { ProductName },
    props: {
        placeholder: {
            type: String,
            default: "",
        },
        searchParams: {
            type: Object,
            default: () => ({}),
        },
        excludeProductIds: {
            type: Array,
            default: () => [],
        },
        minChars: {
            type: Number,
            default: 2,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            query: "",
            suggestions: [],
            loading: false,
            menuOpen: false,
            highlightedIndex: -1,
            debounceTimer: null,
            cancelSource: null,
        };
    },
    computed: {
        isQueryFilled() {
            return Boolean(this.query && String(this.query).trim());
        },
    },
    beforeDestroy() {
        this.clearDebounce();
        this.cancelRequest();
    },
    methods: {
        focus() {
            const field = this.$refs.field;
            if (field && typeof field.focus === "function") {
                field.focus();
                return;
            }
            const input = field && field.$el && field.$el.querySelector("input");
            if (input) {
                input.focus();
            }
        },
        clearDebounce() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }
        },
        cancelRequest() {
            if (this.cancelSource) {
                this.cancelSource.cancel("canceled");
                this.cancelSource = null;
            }
        },
        onQueryInput(value) {
            this.query = value || "";
            this.highlightedIndex = -1;
            this.clearDebounce();
            if (!this.query || this.query.length < this.minChars) {
                this.cancelRequest();
                this.suggestions = [];
                this.loading = false;
                this.menuOpen = !!this.query;
                return;
            }
            this.menuOpen = true;
            this.loading = true;
            this.debounceTimer = setTimeout(() => {
                this.fetchSuggestions();
            }, 250);
        },
        onFocus() {
            if (this.query.length >= this.minChars) {
                this.menuOpen = true;
            }
        },
        onBlur() {
            window.setTimeout(() => {
                this.menuOpen = false;
                this.highlightedIndex = -1;
            }, 150);
        },
        onClear() {
            this.query = "";
            this.suggestions = [];
            this.menuOpen = false;
            this.highlightedIndex = -1;
            this.cancelRequest();
        },
        closeMenu() {
            this.menuOpen = false;
            this.highlightedIndex = -1;
        },
        async fetchSuggestions() {
            this.cancelRequest();
            const cancelSource = axios.CancelToken.source();
            this.cancelSource = cancelSource;

            try {
                const response = await axios.get("/api/products/", {
                    params: {
                        search: this.query,
                        limit: 8,
                        ordering: "name",
                        stock_tracking: true,
                        ...this.searchParams,
                    },
                    cancelToken: cancelSource.token,
                });
                const excluded = new Set(
                    (this.excludeProductIds || []).map((id) => String(id)),
                );
                this.suggestions = (response.data.results || []).filter(
                    (product) => !excluded.has(String(product.id)),
                );
                this.menuOpen = true;
            } catch (error) {
                if (!axios.isCancel(error)) {
                    this.suggestions = [];
                }
            } finally {
                if (this.cancelSource === cancelSource) {
                    this.loading = false;
                    this.cancelSource = null;
                }
            }
        },
        selectProduct(product) {
            if (!product?.id) {
                return;
            }
            this.$emit("product-select", {
                id: product.id,
                upc: product.upc,
                name: product.name,
            });
            this.onClear();
            this.$refs.field?.blur?.();
        },
        focusNext() {
            if (!this.suggestions.length) {
                return;
            }
            this.highlightedIndex =
                (this.highlightedIndex + 1) % this.suggestions.length;
        },
        focusPrevious() {
            if (!this.suggestions.length) {
                return;
            }
            this.highlightedIndex =
                this.highlightedIndex <= 0
                    ? this.suggestions.length - 1
                    : this.highlightedIndex - 1;
        },
        selectHighlighted() {
            if (
                this.highlightedIndex >= 0 &&
                this.suggestions[this.highlightedIndex]
            ) {
                this.selectProduct(this.suggestions[this.highlightedIndex]);
            }
        },
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading…",
                noResults: "No products found",
            },
            fr: {
                loading: "Chargement…",
                noResults: "Aucun produit trouvé",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.table-search-product-autocomplete {
    position: relative;
    min-width: 0;

    &__field {
        ::v-deep > .v-input__control > .v-input__slot {
            align-items: center !important;
            min-height: 40px !important;
        }

        ::v-deep .v-input__prepend-inner .v-icon {
            font-size: 20px !important;
            color: $color-gs-semibold !important;
        }

        &--filled {
            ::v-deep > .v-input__control > .v-input__slot {
                background: $color-primary-extralight;
            }

            &:not(.v-input--is-focused) ::v-deep fieldset {
                border-color: $color-primary-light !important;
            }

            ::v-deep input {
                color: $color-primary-dark;
            }

            ::v-deep .v-input__prepend-inner .v-icon,
            ::v-deep .v-input__append-inner .v-icon {
                color: $color-primary-dark !important;
            }
        }
    }

    &__menu {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        right: 0;
        z-index: 8;
        max-height: 320px;
        overflow-y: auto;
    }

    &__item--active {
        background: rgba($color-primary, 0.08);
    }
}
</style>
